import { ID, TaskFilterOptions, TaskLinkIndex } from '@griply/types';

const linkIdsIn = (filter: TaskFilterOptions): ID[] => [
  ...(filter.goals ?? []),
  ...(filter.lifeAreas ?? []),
];

// Filter sets are a union, so a set without a link narrowing can still match anything —
// narrowing is only safe when every set names the links it wants.
export const narrowTasksByLinkIndex = <T extends { id: ID }>(
  tasks: T[],
  filters: TaskFilterOptions[],
  index: TaskLinkIndex<T>,
): T[] => {
  if (!filters.length || filters.some((filter) => !linkIdsIn(filter).length)) {
    return tasks;
  }

  const buckets = filters.flatMap((filter) => [
    ...(filter.goals ?? []).map((goalId) => index.byGoalId.get(goalId)),
    ...(filter.lifeAreas ?? []).map((lifeAreaId) =>
      index.byLifeAreaId.get(lifeAreaId),
    ),
  ]);
  const present = buckets.filter((bucket): bucket is T[] => !!bucket);

  // One bucket: it is already in source order, and handing back its own array keeps
  // the identity that lets downstream memos skip an unchanged goal.
  if (present.length === 1) {
    return present[0];
  }

  const matched = new Set<ID>(
    present.flatMap((bucket) => bucket.map(({ id }) => id)),
  );

  // Filtering the source keeps the original ordering the callers rely on.
  return tasks.filter(({ id }) => matched.has(id));
};
